/**
 * Ereignisse (Fire Events)
 * @module Events
 */

import React, { Component } from 'react';
import SecuredZone from '../components/SecuredZone';
import API from '../api/api';
import LinearProgress from '@mui/material/LinearProgress';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import TablePagination from '@mui/material/TablePagination';
import Button from '@mui/material/Button';
import { Icon, IconWell } from '../icons/AppIcons';
import moment from 'moment';
import { handleApiError } from '../utils/errorHandler';
import { Link } from 'react-router-dom';
import { resetAlarmWithTrace } from '../utils/actionTrace';

const FireApi = new API();

class Events extends Component {
  constructor(props) {
    super(props);
    this.state = {
      events: [],
      loading: true,
      confirmingId: null,
      page: 0,
      rowsPerPage: 25,
      totalCount: 0,
      filterConfirmed: '',
    };
  }

  loadData(page = null, rowsPerPage = null) {
    const currentPage = page !== null ? page : this.state.page;
    const currentRowsPerPage = rowsPerPage !== null ? rowsPerPage : this.state.rowsPerPage;
    const pagination = {
      page: currentPage + 1,
      page_size: currentRowsPerPage,
    };
    if (this.state.filterConfirmed !== '') {
      pagination.confirmed = this.state.filterConfirmed;
    }

    this.setState({ loading: true });
    FireApi.endpoints.events.getAll({ withCredentials: true, crossDomain: true }, pagination)
      .then((res) => {
        let list = [];
        let totalCount = 0;
        if (res?.data?.results && Array.isArray(res.data.results)) {
          list = res.data.results;
          totalCount = res.data.count || list.length;
        } else if (Array.isArray(res?.data)) {
          list = res.data;
          totalCount = list.length;
        }
        this.setState({ events: list, totalCount, loading: false });
      })
      .catch((error) => {
        handleApiError(error, 'beim Laden der Ereignisse', () => {
          this.setState({ events: [], totalCount: 0, loading: false });
        });
      });
  }

  confirmEvent(eventId, event) {
    this.setState({ confirmingId: eventId });
    resetAlarmWithTrace(FireApi, eventId, event)
      .then((data) => {
        if (data?.status === 'success') {
          const confirmedIds = Array.isArray(data.confirmed_ids) ? data.confirmed_ids.map(Number) : [];
          const deviceId = data.historical ? null : data.event?.device_id;
          this.setState((prev) => {
            const events = prev.events.map((ev) => (
              confirmedIds.includes(Number(ev.id))
                || ev.id === eventId
                || (deviceId && ev.device_id === deviceId)
                ? { ...ev, confirmed: true }
                : ev
            ));
            const visible = prev.filterConfirmed === '0'
              ? events.filter((ev) => !ev.confirmed)
              : events;
            return {
              confirmingId: null,
              events: visible,
              totalCount: Math.max(0, prev.totalCount - (events.length - visible.length)),
            };
          });
          return;
        }
        this.setState({ confirmingId: null });
      })
      .catch(() => {
        this.setState({ confirmingId: null });
      });
  }

  handleChangePage = (event, newPage) => {
    this.setState({ page: newPage }, () => this.loadData(newPage, null));
  };

  handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    this.setState({ rowsPerPage: newRowsPerPage, page: 0 }, () => this.loadData(0, newRowsPerPage));
  };

  setFilter = (value) => {
    this.setState({ filterConfirmed: value, page: 0 }, () => this.loadData(0, null));
  };

  componentDidMount() {
    this.loadData();
  }

  renderAdapter(value) {
    return value ? (
      <span className="fc-chip is-alarm"><Icon name="alarm" size={18} /> Alarm</span>
    ) : (
      <span className="fc-chip is-ok"><Icon name="ok" size={18} /> OK</span>
    );
  }

  render() {
    const { events, loading, confirmingId, filterConfirmed } = this.state;

    return (
      <SecuredZone
        title="Ereignisse"
        breadcrumbs={{ '/': 'Dashboard', '/events': 'Ereignisse' }}
        loading={loading ? <LinearProgress color="primary" /> : <span className="loader-span"></span>}
      >
        <div className="filter-bar">
          <Button
            size="small"
            startIcon={<Icon name="inbox" size={18} />}
            variant={filterConfirmed === '' ? 'contained' : 'outlined'}
            onClick={() => this.setFilter('')}
          >
            Alle
          </Button>
          <Button
            size="small"
            startIcon={<Icon name="alarm" size={18} />}
            variant={filterConfirmed === '0' ? 'contained' : 'outlined'}
            onClick={() => this.setFilter('0')}
          >
            Offen
          </Button>
          <Button
            size="small"
            startIcon={<Icon name="confirmed" size={18} />}
            variant={filterConfirmed === '1' ? 'contained' : 'outlined'}
            onClick={() => this.setFilter('1')}
          >
            Bestätigt
          </Button>
        </div>

        <TableContainer className="settings-table" component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Zeit</TableCell>
                <TableCell>Koffer</TableCell>
                <TableCell>Typ</TableCell>
                <TableCell>Anschluss 1</TableCell>
                <TableCell>Anschluss 2</TableCell>
                <TableCell>Anschluss 3</TableCell>
                <TableCell>Anschluss 4</TableCell>
                <TableCell>Netzzeit</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Aktion</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {events.length > 0 ? (
                events.map((ev) => (
                  <TableRow key={ev.id}>
                    <TableCell>{ev.created ? moment(ev.created).format('DD.MM.YYYY HH:mm:ss') : '-'}</TableCell>
                    <TableCell>
                      {ev.device_id ? (
                        <Link to={`/devices/${ev.device_id}/`} className="fc-name-cell">
                          <IconWell name="koffer" tone="accent" size={22} />
                          {ev.device_name || ev.device_uuid || ev.device_id}
                        </Link>
                      ) : (
                        ev.device_name || '-'
                      )}
                    </TableCell>
                    <TableCell>{ev.type || '-'}</TableCell>
                    <TableCell>{this.renderAdapter(ev.adapter_a)}</TableCell>
                    <TableCell>{this.renderAdapter(ev.adapter_b)}</TableCell>
                    <TableCell>{this.renderAdapter(ev.adapter_c)}</TableCell>
                    <TableCell>{this.renderAdapter(ev.adapter_d)}</TableCell>
                    <TableCell>{ev.network_time ? moment(ev.network_time).format('DD.MM.YYYY HH:mm:ss') : '-'}</TableCell>
                    <TableCell>
                      {ev.confirmed ? (
                        <span className="fc-chip is-ok"><Icon name="confirmed" size={18} /> Bestätigt</span>
                      ) : (
                        <span className="fc-chip is-alarm"><Icon name="alarm" size={18} /> Offen</span>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      {!ev.confirmed && (
                        <Button
                          size="small"
                          variant="contained"
                          color="primary"
                          startIcon={<Icon name="confirmed" size={18} />}
                          disabled={confirmingId === ev.id}
                          onClick={(event) => this.confirmEvent(ev.id, event)}
                        >
                          Zurückstellen
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={10} align="center" style={{ padding: '60px 20px' }}>
                    {loading ? (
                      <div>Lade Ereignisse...</div>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <Icon name="events" size={64} sx={{ opacity: 0.4, mb: 1 }} />
                        <div>Keine Ereignisse gefunden</div>
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={this.state.totalCount}
          page={this.state.page}
          onPageChange={this.handleChangePage}
          rowsPerPage={this.state.rowsPerPage}
          onRowsPerPageChange={this.handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 25, 50, 100]}
          labelRowsPerPage="Zeilen pro Seite:"
          labelDisplayedRows={({ page }) => {
            const totalPages = this.state.totalCount > 0 ? Math.ceil(this.state.totalCount / this.state.rowsPerPage) : 1;
            return `Seite ${page + 1} von ${totalPages}`;
          }}
          className="table-pagination"
        />
      </SecuredZone>
    );
  }
}

export default Events;
